import { apiClient } from '@/api/api'
import type { AxiosResponse } from 'axios'
import type { UserProfile } from '@/types/user'
import type { ApiProfileGender } from '@/features/profile/gender'

export const fetchUserProfile = (): Promise<AxiosResponse<UserProfile>> => {
  return apiClient.get<UserProfile>('/user/profile')
}

/**
 * `GET /auth/user-info` on the wire. The `auth/` prefix is EXCLUDED from the
 * case transformer (`caseTransformerInterceptor.ts`), so the two survey ids
 * arrive in snake_case and are `int | None` on the backend
 * (`usecases/auth/user_info.py`).
 */
export interface UserInfoWire {
  name: string
  email: string
  taste_survey_config_id: number | null
  size_survey_config_id: number | null
}

/** What the hook serves: mapped explicitly below, since the interceptor does not. */
export interface UserInfoResponse {
  name: string
  email: string
  tasteSurveyConfigId: number | null
  sizeSurveyConfigId: number | null
}

export const mapUserInfo = (wire: UserInfoWire): UserInfoResponse => ({
  name: wire.name,
  email: wire.email,
  tasteSurveyConfigId: wire.taste_survey_config_id ?? null,
  sizeSurveyConfigId: wire.size_survey_config_id ?? null,
})

export const fetchAuthUserInfo = async (): Promise<AxiosResponse<UserInfoResponse>> => {
  const response = await apiClient.get<UserInfoWire>('/auth/user-info')
  return { ...response, data: mapUserInfo(response.data) }
}

// Types for new avatar upload endpoints
export interface GetAvatarUploadUrlRequest {
  content_type: string
  file_size: number
}

// Note: Response is transformed from snake_case to camelCase by caseTransformerInterceptor
export interface GetAvatarUploadUrlResponse {
  uploadUrl: string
  key: string
  expiresIn: number
}

export interface ConfirmAvatarUploadRequest {
  key: string
}

// Note: Response is transformed from snake_case to camelCase by caseTransformerInterceptor
export interface ConfirmAvatarUploadResponse {
  avatarUrl?: string
  avatar?: string
}


export type DeleteAccountReason =
  | 'no_longer_needed'
  | 'found_alternative'
  | 'privacy_concerns'
  | 'too_many_emails'
  | 'difficult_to_use'
  | 'other'

export interface DeleteAccountResponse {
  message: string
  /** Decision 16 — a soft delete; the account can be restored until this date (UTC). */
  recoveryDeadline: string
  canRecover: boolean
  cognitoDisabled: boolean
  /** `scheduled_for_deletion` (additive, 2026-10-20). */
  accountState?: string
  /** The "restore your account" letter went out. */
  restoreEmailSent?: boolean
  /** Decision 17 — a live subscription was cancelled now (`null`: there was none). */
  subscriptionCancelled?: boolean | null
}

/**
 * `DELETE /user` (decision 16): scheduled for deletion, restorable for 30 days from the link in
 * the letter; sign-in is closed at once. The backend accepts a reason and free-text feedback; the
 * redesign asks neither, so the required enum defaults to "other".
 */
export const deleteAccount = (
  reason: DeleteAccountReason = 'other',
  feedback?: string
): Promise<AxiosResponse<DeleteAccountResponse>> => {
  return apiClient.delete<DeleteAccountResponse>('/user', {
    data: { reason, feedback: feedback ?? null }
  })
}

export const deleteUserAvatar = (): Promise<AxiosResponse<void>> => {
  return apiClient.delete<void>('/user/avatar')
}

export const resetUserPreferences = (): Promise<AxiosResponse<void>> => {
  return apiClient.post<void>('/user/preferences/reset')
}

export interface UpdateUserProfileRequest {
  name?: string
  /** `GenderEnum` only — `female` / `male` answer 422 (audit #2). */
  gender?: ApiProfileGender | null
  birthday?: string | null
  profile_img_url?: string | null
  /** BE-186 / BE-201 — only the fields sent are written. */
  units?: 'metric' | 'imperial'
  language?: string
  currency?: string
}

export const updateUserProfile = (data: UpdateUserProfileRequest): Promise<AxiosResponse<UserProfile>> => {
  return apiClient.put<UserProfile>('/user/profile', data)
}

/** `PATCH /user/profile` (BE-201) — a partial write; answers an empty body like the PUT. */
export const patchUserProfile = (data: UpdateUserProfileRequest): Promise<AxiosResponse<void>> => {
  return apiClient.patch<void>('/user/profile', data)
}

// New avatar upload endpoints
export const getAvatarUploadUrl = (
  data: GetAvatarUploadUrlRequest
): Promise<AxiosResponse<GetAvatarUploadUrlResponse>> => {
  return apiClient.post<GetAvatarUploadUrlResponse>('/user/avatar/upload-url', data)
}

export const confirmAvatarUpload = (
  data: ConfirmAvatarUploadRequest
): Promise<AxiosResponse<ConfirmAvatarUploadResponse>> => {
  return apiClient.post<ConfirmAvatarUploadResponse>('/user/avatar/confirm', data)
}

// Helper function to upload file directly to S3
export async function uploadFileToS3(
  file: File,
  uploadUrl: string,
  contentType: string
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to upload file to S3: ${response.statusText}`)
  }
}

// Wrapper function for three-step avatar upload process
export async function uploadAvatar(file: File): Promise<string> {
  const contentType = file.type || 'image/jpeg'
  const fileSize = file.size

  // Step 1: Get presigned upload URL
  const uploadUrlResponse = await getAvatarUploadUrl({
    content_type: contentType,
    file_size: fileSize,
  })

  // Data is transformed from snake_case to camelCase by caseTransformerInterceptor
  const { uploadUrl, key } = uploadUrlResponse.data

  if (!uploadUrl) {
    throw new Error('Upload URL not returned from server')
  }

  // Step 2: Upload file directly to S3
  await uploadFileToS3(file, uploadUrl, contentType)

  // Step 3: Confirm upload
  const confirmResponse = await confirmAvatarUpload({ key })
  // Data is transformed from snake_case to camelCase by caseTransformerInterceptor
  const avatarUrl = confirmResponse.data.avatarUrl || confirmResponse.data.avatar

  if (!avatarUrl) {
    throw new Error('Avatar URL not returned from server')
  }

  return avatarUrl
}

